var _excluded = ["captcha"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
import { mergeDeep } from '@0x234/common-util';
import { TokenChallengeError } from '../errors/token-challenge-error.js';

/**
 * ReCAPTCHA plugin for Vue 3.
 *
 * Integrates ReCAPTCHA v3 into a Vue application and modifies Axios clients to handle CAPTCHA challenges.
 *
 * @type {import('vue').Plugin}
 */
export default {
  /**
   * Install the plugin.
   *
   * @param {import('vue').App} app - Vue application instance
   * @param {Object} options - Plugin options
   * @param {string} options.v3SiteKey - ReCAPTCHA v3 site key
   * @param {import('axios').AxiosInstance[]} options.axiosClients - Axios clients to modify
   */
  install: (app, _ref) => {
    var {
      v3SiteKey,
      axiosClients
    } = _ref;
    app.provide('recaptchaV3SiteKey', v3SiteKey);
    loadRecaptchaScript(v3SiteKey);
    modifyAxiosClients(axiosClients, v3SiteKey);
  }
};

/**
 * Loads the ReCAPTCHA script and adds it to the document head in browser environments.
 *
 * @param {string} v3SiteKey - ReCAPTCHA v3 site key
 */
function loadRecaptchaScript(v3SiteKey) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  var recaptchaScript = document.createElement('script');
  recaptchaScript.src = "https://www.google.com/recaptcha/api.js?render=".concat(v3SiteKey, "&onload=onRecaptchaLoad");
  recaptchaScript.async = true;
  recaptchaScript.defer = true;
  document.head.appendChild(recaptchaScript);
  window.onRecaptchaLoad = () => {
    console.debug('ReCAPTCHA loaded.');
  };
}

/**
 * Modify Axios clients to include CAPTCHA functionality by adding a postWithCaptcha method to each provided Axios
 * client which can automatically handle the CAPTCHA headers to keep functions tidy.
 *
 * @param {import('axios').AxiosInstance[]} axiosClients - Axios clients to modify
 * @param {string} v3SiteKey - ReCAPTCHA v3 site key
 */
function modifyAxiosClients(axiosClients, v3SiteKey) {
  var _loop = function _loop(client) {
    /**
     * Perform a POST request with CAPTCHA verification.
     *
     * @param {string} url - Request URL
     * @param {Object} [data={}] - Request body data
     * @param {Object} [config={}] - Additional request config
     * @param {Object} [config.captcha] - CAPTCHA-related options
     * @param {string} [config.captcha.token] - Pre-existing CAPTCHA token
     * @param {string} [config.captcha.id] - CAPTCHA challenge ID
     * @param {string} [config.captcha.challenge] - CAPTCHA challenge response
     * @returns {Promise<import('axios').AxiosResponse>} Server response
     * @throws {TokenChallengeError} If a CAPTCHA challenge is required
     * @throws {Error} For any other request error
     */
    client.postWithCaptcha = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(function* (url) {
        var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
        try {
          var {
              captcha = {}
            } = config,
            requestConfig = _objectWithoutProperties(config, _excluded);
          var captchaHeaders = yield prepareCaptchaHeaders(captcha, v3SiteKey);
          var mergedConfig = mergeDeep(requestConfig, {
            headers: captchaHeaders
          });
          return yield client.post(url, data, mergedConfig);
        } catch (err) {
          handleCaptchaError(err);
        }
      });
      return function (_x) {
        return _ref2.apply(this, arguments);
      };
    }();
  };
  for (var client of axiosClients) {
    _loop(client);
  }
}

/**
 * Prepare headers for CAPTCHA request.
 *
 * @param {Object} captcha - CAPTCHA data
 * @param {string} [captcha.token] - Pre-existing CAPTCHA token
 * @param {string} [captcha.id] - CAPTCHA challenge ID
 * @param {string} [captcha.challenge] - CAPTCHA challenge response
 * @param {string} v3SiteKey - ReCAPTCHA v3 site key
 * @returns {Promise<Object>} Headers object with CAPTCHA-related headers
 */
function prepareCaptchaHeaders(_x2, _x3) {
  return _prepareCaptchaHeaders.apply(this, arguments);
}
/**
 * Execute a CAPTCHA request to obtain a token.
 *
 * @param {string} v3SiteKey - ReCAPTCHA v3 site key
 * @returns {Promise<string>} CAPTCHA token
 * @throws {Error} If ReCAPTCHA is not loaded or fails to execute
 */
function _prepareCaptchaHeaders() {
  _prepareCaptchaHeaders = _asyncToGenerator(function* (captcha, v3SiteKey) {
    var headers = {};
    if (!captcha.token) {
      captcha.token = yield executeCaptchaRequest(v3SiteKey);
    }
    headers['x-captcha-token'] = captcha.token;
    if (captcha.id && captcha.challenge) {
      headers['x-captcha-id'] = captcha.id;
      headers['x-captcha-challenge'] = captcha.challenge;
    }
    return headers;
  });
  return _prepareCaptchaHeaders.apply(this, arguments);
}
function executeCaptchaRequest(v3SiteKey) {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha) return window.grecaptcha.execute(v3SiteKey, {
      action: 'submit'
    }).then(token => resolve(token)).catch(error => reject(error));
    reject(new Error('ReCAPTCHA not loaded'));
  });
}

/**
 * Handle CAPTCHA-related errors.
 *
 * @param {Error} err - Caught error
 * @param {import('axios').Response} err.response
 * @throws {TokenChallengeError} If the error is a TokenChallengeError
 * @throws {Error} The original error if not a TokenChallengeError
 */
function handleCaptchaError(err) {
  var _err$response, _err$response2;
  if ((err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 ? void 0 : _err$response.status) === 403 && (err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 || (_err$response2 = _err$response2.data) === null || _err$response2 === void 0 ? void 0 : _err$response2.name) === 'TokenChallengeError') {
    throw new TokenChallengeError(err.response.data.message, err.response.data.details);
  }
  throw err;
}